import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Payment as PaymentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/api';

const API_URL = `${API_BASE_URL}/api`;

const SchemeDetailsDialog = ({ open, onClose, schemeId, onRecordPayment, onDataChange }) => {
  const [scheme, setScheme] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && schemeId) {
      fetchSchemeDetails();
    }
  }, [open, schemeId]);

  const fetchSchemeDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/schemes/${schemeId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setScheme(response.data.scheme);
    } catch (err) {
      console.error('Error fetching scheme details:', err);
      setError(err.response?.data?.error?.message || 'Failed to fetch scheme details');
    } finally {
      setLoading(false);
    }
  };

  // Check if scheme is expired
  const isSchemeExpired = (scheme) => {
    return new Date(scheme.ExpiryDate) < new Date();
  };

  // Get dynamic status label based on payment count
  const getDisplayStatus = (scheme) => {
    // If expired, show Expired
    if (isSchemeExpired(scheme)) {
      return 'Expired';
    }
    // If payment count is less than 13, always show Active-Contribution
    if (scheme.PaymentCount < 13) {
      return 'Active-Contribution';
    }
    // Otherwise show the actual status
    return scheme.Status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'Active-Contribution': 'primary',
      'Ready-For-Bonus': 'warning',
      'Active-Usage': 'success',
      'Expired': 'default',
      'Incomplete': 'error'
    };
    return colors[status] || 'default';
  };

  const renderPaymentTimeline = () => {
    if (!scheme) return null;

    const payments = scheme.payments || [];
    const totalSlots = 13;
    const paymentMap = {};
    
    payments.forEach(payment => {
      paymentMap[payment.PaymentNumber] = payment;
    });

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          Payment Timeline (13 Payments)
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          {Array.from({ length: totalSlots }, (_, i) => i + 1).map(num => {
            const payment = paymentMap[num];
            const isPaid = !!payment;
            
            return (
              <Tooltip 
                key={num}
                title={isPaid ? `Payment ${num}: ₹${parseFloat(payment.Amount).toFixed(2)} on ${new Date(payment.PaymentDate).toLocaleDateString()}` : `Payment ${num}: Not paid`}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isPaid ? '#4caf50' : '#e0e0e0',
                    color: isPaid ? 'white' : '#666',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: 2
                    }
                  }}
                >
                  {num}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
        <Box sx={{ mt: 1 }}>
          <LinearProgress 
            variant="determinate" 
            value={(scheme.PaymentCount / 13) * 100}
            sx={{ 
              height: 8, 
              borderRadius: 1,
              bgcolor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                bgcolor: '#4caf50'
              }
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {scheme.PaymentCount} of 13 payments completed ({((scheme.PaymentCount / 13) * 100).toFixed(0)}%)
          </Typography>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Alert severity="error">{error}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!scheme) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#1a1a1a",
          border: "1px solid #333",
          borderRadius: 2,
          mx: { xs: 1, sm: 0 }
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: "#2a2a2a",
        borderBottom: '2px solid #C59A58',
        color: "#fff",
        p: { xs: 1.5, sm: 2 }
      }}>
        <Box>
          <Typography variant="h5" component="div" sx={{ 
            fontWeight: 600, 
            color: '#C59A58',
            fontSize: { xs: "1.1rem", sm: "1.25rem" }
          }}>
            Scheme Details - #{scheme.SchemeId}
          </Typography>
          <Typography variant="caption" sx={{ color: "#888" }}>
            {scheme.CustomerName} • Token #{scheme.CustomerTokenNo}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#C59A58" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#1a1a1a" }}>
        {/* Status and Key Info */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={6} sx={{ 
              bgcolor: "#2a2a2a", 
              border: "1px solid #333",
              height: '100%',
              p: { xs: 1.5, sm: 2 }
            }}>
              <Typography variant="caption" sx={{ color: "#C59A58" }}>Status</Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={getDisplayStatus(scheme)}
                  sx={{
                    bgcolor: getDisplayStatus(scheme) === 'Active-Contribution' ? "#1976d2" :
                             getDisplayStatus(scheme) === 'Ready-For-Bonus' ? "#ed6c02" :
                             getDisplayStatus(scheme) === 'Active-Usage' ? "#2e7d32" :
                             getDisplayStatus(scheme) === 'Expired' ? "#666" : "#d32f2f",
                    color: "white",
                    fontWeight: 600
                  }}
                />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={6} sx={{ 
              bgcolor: "#2a2a2a", 
              border: "1px solid #333",
              height: '100%',
              p: { xs: 1.5, sm: 2 }
            }}>
              <Typography variant="caption" sx={{ color: "#C59A58" }}>Monthly Installment</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#ed6c02' }}>
                ₹{parseFloat(scheme.MonthlyInstallment).toFixed(2)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={6} sx={{ 
              bgcolor: "#2a2a2a", 
              border: "1px solid #333",
              height: '100%',
              p: { xs: 1.5, sm: 2 }
            }}>
              <Typography variant="caption" sx={{ color: "#C59A58" }}>Total Paid</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                ₹{parseFloat(scheme.TotalPaid).toFixed(2)}
              </Typography>
              <Typography variant="caption" sx={{ color: "#888" }}>
                {scheme.PaymentCount} payments
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={6} sx={{ 
              bgcolor: "#2a2a2a", 
              border: "1px solid #C59A58",
              height: '100%',
              p: { xs: 1.5, sm: 2 }
            }}>
              <Typography variant="caption" sx={{ color: "#C59A58" }}>Available Credit</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#9c27b0' }}>
                ₹{(scheme.PaymentCount >= 13 && !isSchemeExpired(scheme)) ? parseFloat(scheme.AvailableCredit).toFixed(2) : '0.00'}
              </Typography>
              <Typography variant="caption" sx={{ color: "#888" }}>
                of ₹{(scheme.PaymentCount >= 13 && !isSchemeExpired(scheme)) ? parseFloat(scheme.TotalCredit || 0).toFixed(2) : '0.00'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Dates */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Paper elevation={6} sx={{ 
              p: 2, 
              bgcolor: "#2a2a2a", 
              border: "1px solid #333"
            }}>
              <Typography variant="caption" sx={{ color: "#C59A58" }}>Start Date</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: "#fff" }}>
                {new Date(scheme.StartDate).toLocaleDateString('en-IN', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper elevation={6} sx={{ 
              p: 2, 
              bgcolor: "#2a2a2a", 
              border: "1px solid #333"
            }}>
              <Typography variant="caption" sx={{ color: "#C59A58" }}>Expiry Date</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: "#fff" }}>
                {new Date(scheme.ExpiryDate).toLocaleDateString('en-IN', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper elevation={6} sx={{ 
              p: 2, 
              bgcolor: "#2a2a2a", 
              border: "1px solid #333"
            }}>
              <Typography variant="caption" sx={{ color: "#C59A58" }}>Last Payment</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: "#fff" }}>
                {scheme.LastPaymentDate 
                  ? new Date(scheme.LastPaymentDate).toLocaleDateString('en-IN', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })
                  : 'No payments yet'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Payment Timeline */}
        <Paper elevation={6} sx={{ 
          p: 2, 
          mb: 3, 
          bgcolor: "#2a2a2a", 
          border: "1px solid #333"
        }}>
          {renderPaymentTimeline()}
        </Paper>

        {/* Bonus Information */}
        {parseFloat(scheme.BonusAmount) > 0 && (
          <Paper elevation={6} sx={{ 
            p: 2, 
            mb: 3, 
            bgcolor: "#2a2a2a", 
            border: '2px solid #ff9800'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#ff9800', mb: 1 }}>
              🎁 Bonus Awarded!
            </Typography>
            <Typography variant="body2" sx={{ color: "#fff" }}>
              Bonus Amount: <strong>₹{parseFloat(scheme.BonusAmount).toFixed(2)}</strong>
            </Typography>
            {scheme.UsagePhaseStartDate && (
              <Typography variant="caption" sx={{ color: "#888" }}>
                Usage phase started on {new Date(scheme.UsagePhaseStartDate).toLocaleDateString()}
              </Typography>
            )}
          </Paper>
        )}

        {/* Credit Usage History */}
        {scheme.creditUsage && scheme.creditUsage.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#C59A58" }}>
              Credit Usage History
            </Typography>
            <TableContainer component={Paper} sx={{ bgcolor: "#2a2a2a", border: "1px solid #333" }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#C59A58" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: "#000" }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#000" }}>Bill Number</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#000" }}>Amount Used</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#000" }}>Bill Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#000" }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scheme.creditUsage.map((usage) => (
                    <TableRow key={usage.UsageId} hover sx={{ 
                      "&:hover": { bgcolor: "#333" },
                      "& td": { color: "#fff", borderColor: "#444" }
                    }}>
                      <TableCell>{new Date(usage.UsageDate).toLocaleDateString()}</TableCell>
                      <TableCell>{usage.BillNumber || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#9c27b0' }}>
                        ₹{parseFloat(usage.AmountUsed).toFixed(2)}
                      </TableCell>
                      <TableCell>₹{parseFloat(usage.BillAmount || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <Typography variant="caption">{usage.Notes || '-'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* No Credit Usage Message */}
        {(!scheme.creditUsage || scheme.creditUsage.length === 0) && scheme.Status === 'Active-Usage' && (
          <Alert 
            severity="info" 
            sx={{ 
              mt: 2,
              bgcolor: "#2a2a2a",
              color: "#fff",
              border: "1px solid #1976d2",
              '& .MuiAlert-icon': { color: "#1976d2" }
            }}
          >
            No credit has been used yet. Available credit: ₹{parseFloat(scheme.AvailableCredit).toFixed(2)}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: 2, 
        bgcolor: "#2a2a2a", 
        borderTop: '1px solid #333' 
      }}>
        <Button 
          onClick={onClose}
          sx={{ 
            color: "#C59A58",
            "&:hover": { bgcolor: "rgba(197, 154, 88, 0.04)" }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SchemeDetailsDialog;
